import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Supplier, SupplierCreate, SupplierFilters, SupplierUpdate } from "@/types/inventory/supplier";
import { addSupplier, deleteSupplier, fetchSupplier, fetchSupplierOptions, fetchSuppliers, updateSupplier } from "@/api/provider/inventory/supplier";

// ─────────────────────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────────────────────

export const supplierQueryKeys = {
  all: ["inventory", "supplier"] as const,
  list: (filters?: SupplierFilters) => [...supplierQueryKeys.all, "list", filters] as const,
  detail: (id: string) => [...supplierQueryKeys.all, "detail", id] as const,
  options: () => [...supplierQueryKeys.all, "options"] as const
};

// ─────────────────────────────────────────────────────────────────────────────
// Query Options
// ─────────────────────────────────────────────────────────────────────────────

export const supplierQueryOptions = {
  list: (filters?: SupplierFilters) => ({
    queryKey: supplierQueryKeys.list(filters),
    queryFn: () => fetchSuppliers(filters)
  }),
  detail: (id: string) => ({
    queryKey: supplierQueryKeys.detail(id),
    queryFn: () => fetchSupplier(id)
  }),
  options: () => ({
    queryKey: supplierQueryKeys.options(),
    queryFn: fetchSupplierOptions
  })
};

// ─────────────────────────────────────────────────────────────────────────────
// Query Hooks
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_PER_PAGE = 20;
interface UseSupplierOptions {
  id: string | undefined;
  enabled?: boolean;
}

/**
 * Fetch a single supplier by ID.
 */
export function useSupplier(t0) {
  const $ = _c(8);
  const {
    id,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== id) {
    t2 = supplierQueryKeys.detail(id ?? "");
    $[0] = id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== id) {
    t3 = () => fetchSupplier(id);
    $[2] = id;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = enabled && !!id;
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      staleTime: 300000
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return useQuery(t5);
}
interface UseSuppliersOptions {
  filters?: SupplierFilters;
  enabled?: boolean;
}

/**
 * Hook to fetch suppliers as a flat list. The backend returns all matching
 * rows in one call when no `perPage` is supplied (callers that need
 * prev/next pagination should use `useSuppliersPaginated` instead).
 *
 * `lazyFetch(filters)` performs an imperative fetch for ad-hoc filter sets
 * (e.g. search-as-you-type) and returns the resulting suppliers.
 */
export function useSuppliers(t0) {
  const $ = _c(19);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    filters,
    enabled: t2
  } = t1;
  const enabled = t2 === undefined ? true : t2;
  const queryClient = useQueryClient();
  let t3;
  if ($[2] !== filters) {
    t3 = supplierQueryKeys.list(filters);
    $[2] = filters;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== filters) {
    t4 = () => fetchSuppliers(filters);
    $[4] = filters;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== enabled || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      enabled,
      staleTime: 3600000
    };
    $[6] = enabled;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    data,
    isLoading
  } = useQuery(t5);
  let t6;
  if ($[10] !== data?.data) {
    t6 = data?.data ?? [];
    $[10] = data?.data;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const suppliers = t6;
  const totalSuppliers = data?.total ?? 0;
  let t7;
  if ($[12] !== queryClient) {
    t7 = async newFilters => {
      const response = await queryClient.fetchQuery({
        queryKey: supplierQueryKeys.list(newFilters),
        queryFn: () => fetchSuppliers(newFilters),
        staleTime: 3600000
      });
      return response?.data ?? [];
    };
    $[12] = queryClient;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const lazyFetch = t7;
  let t8;
  if ($[14] !== isLoading || $[15] !== lazyFetch || $[16] !== suppliers || $[17] !== totalSuppliers) {
    t8 = {
      suppliers,
      isLoading,
      lazyFetch,
      totalSuppliers
    };
    $[14] = isLoading;
    $[15] = lazyFetch;
    $[16] = suppliers;
    $[17] = totalSuppliers;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
interface UseSuppliersPaginatedOptions {
  filters?: SupplierFilters;
  enabled?: boolean;
}
interface UseSuppliersPaginatedResult {
  suppliers: Supplier[];
  isLoading: boolean;
  totalSuppliers: number;
}

/**
 * Hook to fetch suppliers with traditional pagination support (prev/next).
 * Uses the filters.page value directly for page-based navigation.
 * Use this for table views with pagination controls.
 * For unpaginated flat-list use cases (dropdowns, search), use useSuppliers.
 */
export function useSuppliersPaginated(t0) {
  const $ = _c(16);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    filters,
    enabled: t2
  } = t1;
  const enabled = t2 === undefined ? true : t2;
  let t3;
  if ($[2] !== filters) {
    t3 = supplierQueryKeys.list(filters);
    $[2] = filters;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== filters) {
    t4 = async () => {
      const queryFilters = {
        ...filters,
        page: filters?.page ?? 1,
        perPage: filters?.perPage ?? DEFAULT_PER_PAGE,
        sortBy: filters?.sortBy ?? "name",
        sortDirection: filters?.sortDirection ?? "asc"
      };
      const response = await fetchSuppliers(queryFilters);
      return response ?? {
        data: [],
        total: 0
      };
    };
    $[4] = filters;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== enabled || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      enabled
    };
    $[6] = enabled;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    data,
    isLoading
  } = useQuery(t5);
  let t6;
  if ($[10] !== data?.data) {
    t6 = data?.data ?? [];
    $[10] = data?.data;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = data?.total ?? 0;
  let t8;
  if ($[12] !== isLoading || $[13] !== t6 || $[14] !== t7) {
    t8 = {
      suppliers: t6,
      isLoading,
      totalSuppliers: t7
    };
    $[12] = isLoading;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}

// ─────────────────────────────────────────────────────────────────────────────
// Invalidation
// ─────────────────────────────────────────────────────────────────────────────

function useInvalidateSuppliers() {
  const $ = _c(4);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = () => {
      queryClient.invalidateQueries({
        queryKey: supplierQueryKeys.all
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const invalidateAll = t0;
  let t1;
  if ($[2] !== invalidateAll) {
    t1 = {
      invalidateAll
    };
    $[2] = invalidateAll;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutation Hooks
// ─────────────────────────────────────────────────────────────────────────────

export function useCreateSupplier() {
  const $ = _c(2);
  const {
    invalidateAll
  } = useInvalidateSuppliers();
  let t0;
  if ($[0] !== invalidateAll) {
    t0 = {
      mutationFn: _temp,
      onSettled: () => {
        invalidateAll();
      }
    };
    $[0] = invalidateAll;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp(data) {
  return addSupplier(data);
}
export function useUpdateSupplier() {
  const $ = _c(2);
  const {
    invalidateAll
  } = useInvalidateSuppliers();
  let t0;
  if ($[0] !== invalidateAll) {
    t0 = {
      mutationFn: _temp2,
      onSettled: () => {
        invalidateAll();
      }
    };
    $[0] = invalidateAll;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp2(t0) {
  const {
    supplierId,
    data
  } = t0;
  return updateSupplier(supplierId, data);
}
export function useDeleteSupplier() {
  const $ = _c(2);
  const {
    invalidateAll
  } = useInvalidateSuppliers();
  let t0;
  if ($[0] !== invalidateAll) {
    t0 = {
      mutationFn: _temp3,
      onSettled: () => {
        invalidateAll();
      }
    };
    $[0] = invalidateAll;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp3(supplierId) {
  return deleteSupplier(supplierId);
}